import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  SelectChangeEvent
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { UserSegment } from '../types';

const Profile: React.FC = () => {
  const { userData, updateUserProfile } = useAuth();
  
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    photoURL: '',
    segment: 'personal' as UserSegment
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (userData) {
      setFormData({
        displayName: userData.displayName || '',
        email: userData.email || '',
        photoURL: userData.photoURL || '',
        segment: userData.segment || 'personal'
      });
    }
  }, [userData]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };
  
  const handleEditToggle = () => {
    setIsEditing(prev => !prev);
    setError(null);
    setSuccess(null);
    
    // Reset form data if canceling edit
    if (isEditing && userData) {
      setFormData({
        displayName: userData.displayName || '',
        email: userData.email || '',
        photoURL: userData.photoURL || '',
        segment: userData.segment || 'personal'
      });
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    try {
      setLoading(true);
      
      // Update user profile
      await updateUserProfile({
        displayName: formData.displayName,
        photoURL: formData.photoURL,
        segment: formData.segment
      });
      
      setSuccess('Profile updated successfully');
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };
  
  const segmentLabels = {
    personal: 'Personal',
    'self-employed': 'Self-Employed',
    llc: 'LLC',
    's-corp': 'S-Corporation'
  };
  
  if (!userData) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1">
              User Profile
            </Typography>
            <Button
              variant={isEditing ? "outlined" : "contained"}
              color={isEditing ? "secondary" : "primary"}
              startIcon={isEditing ? null : <EditIcon />}
              onClick={handleEditToggle}
            >
              {isEditing ? 'Cancel' : 'Edit Profile'}
            </Button>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Avatar
                  src={formData.photoURL || undefined}
                  alt={formData.displayName}
                  sx={{ width: 120, height: 120, mb: 2 }}
                >
                  {!formData.photoURL && <PersonIcon sx={{ fontSize: 60 }} />}
                </Avatar>
                
                {isEditing && (
                  <TextField
                    name="photoURL"
                    label="Profile Image URL"
                    value={formData.photoURL}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    helperText="Enter a URL for your profile image"
                  />
                )}
                
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="h6">
                    {userData.displayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {userData.email}
                  </Typography>
                  <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                    {segmentLabels[userData.segment]} Account
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom>
                  Account Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      name="displayName"
                      label="Full Name"
                      value={formData.displayName}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      name="email"
                      label="Email Address"
                      value={formData.email}
                      fullWidth
                      disabled={true}
                      helperText="Email cannot be changed"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl fullWidth disabled={!isEditing}>
                      <InputLabel id="segment-label">Tax Situation</InputLabel>
                      <Select
                        labelId="segment-label"
                        name="segment"
                        value={formData.segment}
                        label="Tax Situation"
                        onChange={(e: SelectChangeEvent) => handleChange(e)}
                      >
                        <MenuItem value="personal">Personal</MenuItem>
                        <MenuItem value="self-employed">Self-Employed</MenuItem>
                        <MenuItem value="llc">LLC</MenuItem>
                        <MenuItem value="s-corp">S-Corporation</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                
                {isEditing && (
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Profile; 